import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Title,
  Text,
  SimpleGrid,
  Stack,
  Group,
  Image,
  UnstyledButton,
} from "@mantine/core";
import {
  IconMicroscope,
  IconStethoscope,
  IconArrowRight,
} from "@tabler/icons-react";
import { useAuthStore } from "../store";
import { usePortalAuthStore } from "../store/portalAuth.store";

const GCP_LOGO_URL =
  "https://i0.wp.com/gulfcoastpath.com/wp-content/uploads/2021/02/cropped-GCP-Logo-v1.png?fit=1628%2C1071&ssl=1";

interface PortalCardProps {
  icon: React.ReactNode;
  accentColor: string;
  subtitle: string;
  title: string;
  description: string;
  onClick: () => void;
}

function PortalCard({
  icon,
  accentColor,
  subtitle,
  title,
  description,
  onClick,
}: PortalCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <UnstyledButton
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: "100%", display: "block" }}
    >
      <Box
        style={{
          background: "rgba(255, 255, 255, 0.97)",
          borderRadius: 16,
          padding: "2rem",
          boxShadow: hovered
            ? "0 24px 64px rgba(0,0,0,0.28), 0 8px 24px rgba(0,0,0,0.16)"
            : "0 4px 24px rgba(0,0,0,0.14)",
          transform: hovered ? "translateY(-6px)" : "translateY(0)",
          transition:
            "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
          border: `1px solid ${
            hovered ? `${accentColor}50` : "rgba(255,255,255,0.4)"
          }`,
          cursor: "pointer",
          height: "100%",
        }}
      >
        <Stack gap="lg">
          <Box
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: `${accentColor}1a`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: accentColor,
            }}
          >
            {icon}
          </Box>

          <Stack gap={6}>
            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: accentColor,
              }}
            >
              {subtitle}
            </Text>
            <Title
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontWeight: 400,
                fontSize: "1.65rem",
                color: "#1e1e3f",
                lineHeight: 1.15,
              }}
            >
              {title}
            </Title>
            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: "0.9rem",
                color: "#6b7280",
                lineHeight: 1.6,
              }}
            >
              {description}
            </Text>
          </Stack>

          <Group
            gap={6}
            align="center"
            style={{
              color: accentColor,
              marginTop: 4,
            }}
          >
            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 14,
                fontWeight: 500,
                color: accentColor,
              }}
            >
              Continue
            </Text>
            <IconArrowRight
              size={16}
              color={accentColor}
              style={{
                transform: hovered ? "translateX(5px)" : "translateX(0)",
                transition: "transform 200ms ease",
              }}
            />
          </Group>
        </Stack>
      </Box>
    </UnstyledButton>
  );
}

export default function PortalSelector() {
  const navigate = useNavigate();
  const isLisAuth = useAuthStore((s) => s.isAuthenticated);
  const isPortalAuth = usePortalAuthStore((s) => s.isAuthenticated);

  if (isLisAuth) return <Navigate to="/dashboard" replace />;
  if (isPortalAuth) return <Navigate to="/portal/dashboard" replace />;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        @keyframes selectorFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .selector-enter { animation: selectorFadeIn 0.5s ease-out forwards; }
      `}</style>

      <Box
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(160deg, #04119A 0%, #0d1fb5 60%, #1a2cc4 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(1.5rem, 5vw, 3rem)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          style={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            bottom: 60,
            left: -60,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: "rgba(16,185,129,0.1)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            top: "40%",
            right: 80,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.03)",
          }}
        />
        <Box
          style={{
            position: "absolute",
            top: "18%",
            left: "12%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(16,185,129,0.07)",
          }}
        />

        <Box
          className="selector-enter"
          style={{
            width: "100%",
            maxWidth: 780,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Header */}
          <Stack gap="xs" align="center" mb="3rem">
            <Image
              src={GCP_LOGO_URL}
              alt="Gulf Coast Pathologists"
              w={72}
              style={{ filter: "brightness(0) invert(1)", opacity: 0.88 }}
            />
            <Title
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontWeight: 400,
                fontSize: "clamp(1.9rem, 4vw, 2.6rem)",
                color: "#fff",
                textAlign: "center",
                lineHeight: 1.1,
                marginTop: 16,
              }}
            >
              Gulf Coast Pathologists
            </Title>
            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                color: "rgba(255,255,255,0.55)",
                fontSize: "1rem",
                textAlign: "center",
              }}
            >
              Select a portal to continue
            </Text>
          </Stack>

          {/* Portal cards */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
            <PortalCard
              icon={<IconMicroscope size={28} />}
              accentColor="#10b981"
              subtitle="Laboratory"
              title="Staff Portal"
              description="For laboratory staff and administrators. Access cases, workflows, billing, and reports."
              onClick={() => navigate("/login")}
            />
            <PortalCard
              icon={<IconStethoscope size={28} />}
              accentColor="#6470d4"
              subtitle="Physician Access"
              title="Physician Portal"
              description="For physicians to securely view and download finalized pathology reports."
              onClick={() => navigate("/portal/login")}
            />
          </SimpleGrid>

          {/* Footer */}
          <Text
            ta="center"
            mt="2.5rem"
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 12,
              color: "rgba(255,255,255,0.28)",
              letterSpacing: "0.05em",
            }}
          >
            Gulf Coast Pathologists · Confidential
          </Text>
        </Box>
      </Box>
    </>
  );
}
